import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';
import type { Ref } from 'vue';

import {
	useElementHover,
	useEventListener,
	useIntersectionObserver,
	useTimeoutFn
} from '@vueuse/core';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';

interface UseVideoCardPlayerOptions {
	videoUrl: Ref<string>;
	isActive: Ref<boolean>;
}

export function useVideoCardPlayer({
	videoUrl,
	isActive
}: UseVideoCardPlayerOptions) {
	const cardRef = ref<HTMLDivElement | null>(null);
	const videoRef = ref<HTMLVideoElement | null>(null);
	const shakaPlayer = shallowRef<ShakaPlayer | null>(null);

	const isHovered = useElementHover(cardRef);
	const isVideoPlaying = ref(false);
	const isVisible = ref(false);
	const isPlayerReady = ref(false);
	const isInitializing = ref(false);
	const videoProgress = ref(0);
	const isMuted = ref(true);
	let initGeneration = 0;

	const shouldPlay = computed(() => isHovered.value || isActive.value);
	const isLoading = computed(() => shouldPlay.value && !isPlayerReady.value);
	const proxiedVideoUrl = computed(() => getProxiedVideoUrl(videoUrl.value));

	function handleMuteToggle() {
		isMuted.value = !isMuted.value;
		if (videoRef.value) {
			videoRef.value.muted = isMuted.value;
		}
	}

	function seekTo(ratio: number) {
		if (videoRef.value && videoRef.value.duration) {
			videoRef.value.currentTime = ratio * videoRef.value.duration;
		}
	}

	useEventListener(videoRef, 'playing', () => {
		isVideoPlaying.value = true;
	});

	useEventListener(videoRef, 'pause', () => {
		isVideoPlaying.value = false;
	});

	useEventListener(videoRef, 'timeupdate', () => {
		if (videoRef.value && videoRef.value.duration) {
			videoProgress.value =
				(videoRef.value.currentTime / videoRef.value.duration) * 100;
		}
	});

	const { start: startPreload, stop: stopPreload } = useTimeoutFn(
		() => {
			if (isVisible.value && !isPlayerReady.value) {
				initPlayer();
			}
		},
		300,
		{ immediate: false }
	);

	useIntersectionObserver(
		cardRef,
		entries => {
			const entry = entries[0];
			if (!entry) return;

			if (entry.isIntersecting) {
				isVisible.value = true;
				startPreload();
			} else {
				isVisible.value = false;
				stopPreload();
				cleanupPlayer();
			}
		},
		{ rootMargin: '100px' }
	);

	async function initPlayer() {
		if (!videoRef.value || isPlayerReady.value || isInitializing.value) return;

		isInitializing.value = true;
		const generation = ++initGeneration;

		try {
			const player = await createPlayer(proxiedVideoUrl.value, videoRef.value);

			if (generation !== initGeneration || !videoRef.value) {
				destroyPlayer(player);
				return;
			}

			shakaPlayer.value = player;
			isPlayerReady.value = true;

			if (shouldPlay.value && videoRef.value) {
				videoRef.value.play().catch(() => {});
			}
		} catch {
			// Ignore init errors
		} finally {
			isInitializing.value = false;
		}
	}

	function cleanupPlayer() {
		initGeneration++;
		if (shakaPlayer.value) {
			destroyPlayer(shakaPlayer.value);
			shakaPlayer.value = null;
			isPlayerReady.value = false;
			isVideoPlaying.value = false;
			videoProgress.value = 0;
		}
	}

	watch(shouldPlay, playing => {
		if (!videoRef.value) return;

		if (playing) {
			if (!isPlayerReady.value) {
				stopPreload();
				initPlayer();
			} else {
				videoRef.value.play().catch(() => {});
			}
		} else if (isPlayerReady.value) {
			videoRef.value.pause();
		}
	});

	onBeforeUnmount(() => {
		stopPreload();
		cleanupPlayer();
	});

	return {
		cardRef,
		videoRef,
		isVideoPlaying,
		isVisible,
		isLoading,
		shouldPlay,
		videoProgress,
		isMuted,
		handleMuteToggle,
		seekTo
	};
}
